import React, { useState } from 'react';
import { saveSiteMappings, saveEverWashMappings } from '../services/plService';

// Shown after an upload when the file contained site identifiers the backend
// didn't know how to route (no mapping yet). The user picks a Copilot location
// for each; on save the mappings are persisted and the frontend re-runs the
// same upload so the previously skipped rows are ingested.
//
// Shared by the CryptoPay flow (keyed on `site_id`) and the EverWash flow
// (keyed on `service_location_id`) — the two feeds differ only in the name of
// the identifier column and which endpoint stores the mapping.
const UnmappedSitesModal = ({
  unmappedSites,
  locations,
  onCancel,
  onSaved,
  idField = 'site_id',
  source = 'CryptoPay',
}) => {
  const [choices, setChoices] = useState(() => {
    const seed = {};
    for (const s of unmappedSites) seed[s[idField]] = locations[0]?.id || '';
    return seed;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isEverWash = idField === 'service_location_id';
  const setChoice = (siteId, locId) => setChoices((prev) => ({ ...prev, [siteId]: locId }));

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      const mappings = unmappedSites.map((s) => ({
        [idField]: s[idField],
        site_name: s.site_name,
        location_id: Number(choices[s[idField]]),
      }));
      await (isEverWash ? saveEverWashMappings(mappings) : saveSiteMappings(mappings));
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save site mappings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Which wash is this?</h2>
          <p className="text-xs text-gray-500 mt-1">
            This {source} file covers sites we haven't seen yet. Tell us which of your washes each
            one belongs to. We'll remember the mapping and route the rows on this and every
            future upload — click Save and we'll re-ingest the file automatically.
          </p>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">{error}</div>}
          {unmappedSites.map((s) => (
            <div key={s[idField]} className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-center">
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">{s.site_name || '(unnamed site)'}</div>
                <div className="text-xs text-gray-500 font-mono">
                  {isEverWash ? 'Service Location' : 'SiteID'}: {s[idField]}
                </div>
                <div className="text-xs text-gray-500">
                  {isEverWash
                    ? `$${(s.total_revenue ?? 0).toFixed(2)} revenue in this report`
                    : `${s.count} transaction${s.count === 1 ? '' : 's'} in this upload`}
                </div>
              </div>
              <select
                value={choices[s[idField]] || ''}
                onChange={(e) => setChoice(s[idField], e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>{l.business_name || l.display_label || `Location ${l.id}`}</option>
                ))}
              </select>
            </div>
          ))}
        </div>

        <div className="px-6 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onCancel} className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded text-sm font-medium">
            Skip for now
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded text-sm font-medium"
          >
            {saving ? 'Saving…' : 'Save & re-ingest'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnmappedSitesModal;
